/**
 * Hooks de revisão de catalog_stage_products — TH-55150 (M-seed.7).
 *
 * Contrato do backend (`apps/api/priceshow_api/routers/catalog_stage.py`):
 *
 *   GET    /catalog/stage-products?status&source&brand_name&supplier_name&page&per_page
 *   POST   /catalog/stage-products/{id}/approve
 *   POST   /catalog/stage-products/{id}/reject
 *   POST   /catalog/stage-products/bulk-approve  { ids: UUID[] }
 *   POST   /catalog/stage-products/bulk-reject   { ids: UUID[] }
 *
 * Apenas super_admin. Demais roles recebem 403.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

export type StageSource =
  | "apas"
  | "gs1"
  | "fabricante"
  | "cosmos"
  | "off"
  | "manual";

export type StageStatus = "pending" | "approved" | "rejected" | "expired";

export interface StageEan {
  ean: string;
  ean_type: string;
  check_digit_valid: boolean | null;
}

export interface StageProduct {
  id: string;
  source: StageSource;
  source_url: string | null;
  external_id: string | null;
  brand_name: string | null;
  supplier_name: string | null;
  product_name: string;
  description: string | null;
  package_size: string | null;
  category_hint: string | null;
  image_url: string | null;
  status: StageStatus;
  needs_official_image: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  eans: StageEan[];
}

export interface StageListMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface StageListResponse {
  data: StageProduct[];
  meta: StageListMeta;
}

export interface StageListFilters {
  status?: StageStatus;
  source?: StageSource;
  brand_name?: string;
  supplier_name?: string;
  page?: number;
  per_page?: number;
}

export interface BulkResult {
  processed: string[];
  skipped: string[];
  not_found: string[];
  supplier_products_created: number;
  eans_created: number;
}

function buildQuery(filters: StageListFilters): string {
  const sp = new URLSearchParams();
  if (filters.status) sp.set("status", filters.status);
  if (filters.source) sp.set("source", filters.source);
  if (filters.brand_name) sp.set("brand_name", filters.brand_name);
  if (filters.supplier_name) sp.set("supplier_name", filters.supplier_name);
  sp.set("page", String(filters.page ?? 1));
  sp.set("per_page", String(filters.per_page ?? 50));
  return sp.toString();
}

async function fetchStage(
  filters: StageListFilters,
): Promise<StageListResponse> {
  const res = await fetch(
    `/api/catalog/stage-products?${buildQuery(filters)}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as StageListResponse;
}

async function approveStage(id: string): Promise<void> {
  const res = await fetch(`/api/catalog/stage-products/${id}/approve`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

async function rejectStage(id: string): Promise<void> {
  const res = await fetch(`/api/catalog/stage-products/${id}/reject`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

async function bulkApprove(ids: string[]): Promise<BulkResult> {
  const res = await fetch(`/api/catalog/stage-products/bulk-approve`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as BulkResult;
}

async function bulkReject(ids: string[]): Promise<BulkResult> {
  const res = await fetch(`/api/catalog/stage-products/bulk-reject`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as BulkResult;
}

export function useCatalogStage(
  filters: StageListFilters,
): UseQueryResult<StageListResponse, Error> {
  return useQuery({
    queryKey: ["catalog-stage", filters],
    queryFn: () => fetchStage(filters),
  });
}

export function useApproveStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveStage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-stage"] });
    },
  });
}

export function useRejectStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rejectStage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-stage"] });
    },
  });
}

export function useBulkApproveStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkApprove(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-stage"] });
    },
  });
}

export function useBulkRejectStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkReject(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-stage"] });
    },
  });
}
